import { Card, Delimiter, Typography } from '@otp/ui';
import Hidden from '@mui/material/Hidden';
import clsx from 'clsx';
import { User as UserModel, UserLevel } from 'src/graphql/generated/graphql';
import styles from './index.module.css';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@otp/icons';
import { getFullName, getUserLevels } from 'src/utils/user-data';
import { UserLevelColumn } from 'src/components/common/table/columns/level';
import { useDeviceContext } from 'src/context/device.context';

type UserCardProps = {
  user: Partial<UserModel>;
};

export const UserCard = ({ user }: UserCardProps) => {
  const { t } = useTranslation();
  const fullName = getFullName(user);
  const { isMobile } = useDeviceContext();

  const detailItems = [
    {
      label: t('user-details.level', 'Level'),
      value: () =>
        getUserLevels(user)?.map((level) => (
          <UserLevelColumn level={level as UserLevel} />
        )),
      hidden: isMobile,
    },
    {
      label: t('user-details.mobile-phone', 'Mobile phone'),
      value: () => <span>{user.mobilePhone}</span>,
    },
    {
      label: t('user-details.organization', 'Organization'),
      value: () => <span>{user.organization?.name}</span>,
    },
  ].filter((item) => !item.hidden);

  return (
    <Card className={clsx(styles.cardWrapper, 'bg-white flex flex-col w-full')}>
      <div className="flex flex-row md:flex-col gap-1.5 items-center mb-0 md:mb-2">
        <UserAvatar className={styles.titleIcon} />
        <div className="flex flex-col md:items-center gap-0 md:gap-1.5">
          <Typography component="div" variant="body2" fontWeight="bold">
            {fullName}
          </Typography>
          <Typography component="div" variant="subtitle1">
            {user.email}
          </Typography>
          <Typography component="div" variant="subtitle1">
            {user.mcId}
          </Typography>
        </div>
        <Hidden smUp>
          <div className="flex items-start justify-end h-full flex-1">
            {getUserLevels(user).map((level) => (
              <UserLevelColumn key={level} level={level as UserLevel} />
            ))}
          </div>
        </Hidden>
      </div>
      <Delimiter />
      <div>
        {detailItems?.map((item, index) => (
          <Typography
            key={item.label}
            component="div"
            variant="subtitle1"
            className={clsx('flex flex-row justify-between items-center', {
              ['mt-2']: index !== 0,
            })}
          >
            {item.label}
            {typeof item.value === 'function' ? item.value() : item.value}
          </Typography>
        ))}
      </div>
    </Card>
  );
};
